import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Search, Menu, X, LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import PatternOverlay from "@/components/PatternOverlay";
import NavLetters from "@/components/NavLetters";

export default function Header() {
  const { user, logout } = useAuth();
  const { totals, setOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const nav = useNavigate();
  const location = useLocation();

  // Determine which of the shop nav links is currently active.
  // On /shop, only ONE should be highlighted at a time — based on ?category=.
  const currentCategory = new URLSearchParams(location.search).get("category");
  const isShopActive = location.pathname === "/shop" && !currentCategory;
  const isCatActive = (cat) =>
    location.pathname === "/shop" && currentCategory === cat;
  const isLoginActive = location.pathname === "/login";
  const isAdminActive = location.pathname === "/admin";
  const isAccountActive = location.pathname === "/account";

  const linkCls = (active) =>
    `nav-lift-wrap text-xs uppercase tracking-[0.22em] transition-colors ${
      active
        ? "text-primary font-medium"
        : "text-muted-foreground hover:text-primary"
    }`;

  return (
    <header
      data-testid="site-header"
      className="pattern-bg sticky top-0 z-50 backdrop-blur-xl border-b border-border bg-background/95"
    >
      <div
        aria-hidden="true"
        data-testid="header-pattern-layer"
        className="pattern-layer"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/pattern-aexents.png)`,
          opacity: 0.09,
          backgroundSize: "520px auto",
          backgroundPosition: "center top",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-24 flex items-center justify-between relative">
        {/* Left nav */}
        <nav className="hidden md:flex items-center gap-8">
          <Link to="/shop" data-testid="nav-link-shop" data-active={isShopActive} className={linkCls(isShopActive)}>
            <NavLetters text="Shop" />
          </Link>
          <Link to="/shop?category=her" data-testid="nav-link-her" data-active={isCatActive("her")} className={linkCls(isCatActive("her"))}>
            <NavLetters text="Her" />
          </Link>
          <Link to="/shop?category=him" data-testid="nav-link-him" data-active={isCatActive("him")} className={linkCls(isCatActive("him"))}>
            <NavLetters text="Him" />
          </Link>
          <Link to="/shop?category=unisex" data-testid="nav-link-unisex" data-active={isCatActive("unisex")} className={linkCls(isCatActive("unisex"))}>
            <NavLetters text="Unisex" />
          </Link>
        </nav>

        {/* Logo — absolutely centered in viewport */}
        <Link
          to="/"
          data-testid="logo-link"
          className="hidden md:flex items-center select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-auto"
        >
          <img
            src="/logo-aexents.png"
            alt="æxènts London"
            className="h-16 md:h-20 w-auto"
          />
        </Link>

        {/* Mobile logo (in flow) */}
        <Link
          to="/"
          data-testid="logo-link-mobile"
          className="md:hidden flex items-center select-none"
        >
          <img
            src="/logo-aexents.png"
            alt="æxènts London"
            className="h-14 w-auto"
          />
        </Link>

        {/* Right actions */}
        <div className="hidden md:flex items-center gap-6">
          <button
            onClick={() => nav("/shop")}
            className="text-muted-foreground hover:text-primary transition-colors"
            data-testid="header-search-btn"
            aria-label="Search"
          >
            <Search size={18} />
          </button>
          {user && user !== false ? (
            <div className="flex items-center gap-4">
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  data-testid="header-admin-link"
                  data-active={isAdminActive}
                  className={`nav-lift-wrap text-xs uppercase tracking-[0.22em] transition-colors flex items-center gap-1 ${
                    isAdminActive
                      ? "text-primary font-medium"
                      : "text-primary/70 hover:text-primary"
                  }`}
                >
                  <ShieldCheck size={14} /> <NavLetters text="Admin" />
                </Link>
              )}
              <Link
                to="/account"
                data-testid="header-account-link"
                className={`transition-colors ${
                  isAccountActive ? "text-primary" : "text-muted-foreground hover:text-primary"
                }`}
                aria-label="Account"
              >
                <User size={18} />
              </Link>
              <button
                onClick={logout}
                data-testid="header-logout-btn"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Logout"
              >
                <LogOut size={18} />
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="header-login-link"
              data-active={isLoginActive}
              className={`nav-lift-wrap text-xs uppercase tracking-[0.22em] transition-colors ${
                isLoginActive
                  ? "text-primary font-medium"
                  : "text-muted-foreground hover:text-primary"
              }`}
            >
              <NavLetters text="Sign In" />
            </Link>
          )}
          <button
            onClick={() => setOpen(true)}
            data-testid="header-cart-btn"
            aria-label="Open cart"
            className="relative text-muted-foreground hover:text-primary transition-colors"
          >
            <ShoppingBag size={18} />
            {totals.count > 0 && (
              <span
                data-testid="header-cart-count"
                className="absolute -top-2 -right-3 bg-primary text-primary-foreground text-[10px] font-medium px-1.5 min-w-[18px] h-[18px] flex items-center justify-center"
              >
                {totals.count}
              </span>
            )}
          </button>
        </div>

        {/* Mobile */}
        <div className="md:hidden flex items-center gap-4">
          <button
            onClick={() => setOpen(true)}
            data-testid="header-cart-btn-mobile"
            className="relative text-muted-foreground"
          >
            <ShoppingBag size={20} />
            {totals.count > 0 && (
              <span className="absolute -top-2 -right-3 bg-primary text-primary-foreground text-[10px] px-1.5 min-w-[18px] h-[18px] flex items-center justify-center">
                {totals.count}
              </span>
            )}
          </button>
          <button onClick={() => setMenuOpen((v) => !v)} data-testid="mobile-menu-btn">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl">
          <div className="px-6 py-6 flex flex-col gap-5">
            {[
              { to: "/shop", label: "Shop All" },
              { to: "/shop?category=her", label: "Her" },
              { to: "/shop?category=him", label: "Him" },
              { to: "/shop?category=unisex", label: "Unisex" },
              { to: "/account", label: "Account" },
              { to: "/login", label: "Sign In" },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className="text-sm uppercase tracking-[0.22em] text-muted-foreground hover:text-primary"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
